/**
 * Lightweight brand-green confetti for successful USDM mint.
 * No extra dependency — canvas bursts. Respects prefers-reduced-motion.
 * Mobile: denser burst, slightly longer, origin nearer vertical center.
 */

const COLORS = ["#00c853", "#00e676", "#69f0ae", "#b9f6ca", "#f0fff4", "#ffffff"];

function prefersReducedMotion(): boolean {
	if (typeof window === "undefined") return true;
	return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function isNarrowMobile(): boolean {
	return typeof window !== "undefined" && window.innerWidth < 768;
}

function runBurst(): void {
	if (typeof document === "undefined" || prefersReducedMotion()) return;

	const canvas = document.createElement("canvas");
	canvas.setAttribute("aria-hidden", "true");
	canvas.style.cssText =
		"position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999";
	document.body.appendChild(canvas);

	const ctx = canvas.getContext("2d");
	if (!ctx) {
		canvas.remove();
		return;
	}

	const mobile = isNarrowMobile();
	const dpr = Math.min(window.devicePixelRatio || 1, 2);
	const w = window.innerWidth;
	const h = window.innerHeight;
	canvas.width = w * dpr;
	canvas.height = h * dpr;
	ctx.scale(dpr, dpr);

	type P = {
		x: number;
		y: number;
		vx: number;
		vy: number;
		w: number;
		h: number;
		color: string;
		rot: number;
		vr: number;
		life: number;
	};

	const particles: P[] = [];
	const originX = w / 2;
	// Mobile: closer to center (success sheets sit mid-screen)
	const originY = h * (mobile ? 0.42 : 0.28);
	const n = mobile ? 84 : 56;
	for (let i = 0; i < n; i++) {
		const angle = (Math.PI * 2 * i) / n + Math.random() * 0.4;
		const speed = (mobile ? 5 : 4) + Math.random() * (mobile ? 10 : 8);
		particles.push({
			x: originX,
			y: originY,
			vx: Math.cos(angle) * speed,
			vy: Math.sin(angle) * speed - (mobile ? 5 : 4),
			w: 4 + Math.random() * (mobile ? 6 : 5),
			h: 6 + Math.random() * (mobile ? 10 : 8),
			color: COLORS[i % COLORS.length]!,
			rot: Math.random() * Math.PI,
			vr: (Math.random() - 0.5) * 0.3,
			life: 1,
		});
	}

	const start = performance.now();
	const duration = mobile ? 2100 : 1600;

	const frame = (now: number) => {
		const t = (now - start) / duration;
		if (t >= 1) {
			canvas.remove();
			return;
		}
		ctx.clearRect(0, 0, w, h);
		for (const p of particles) {
			p.vy += 0.18;
			p.x += p.vx;
			p.y += p.vy;
			p.vx *= 0.99;
			p.rot += p.vr;
			p.life = 1 - t;
			ctx.save();
			ctx.translate(p.x, p.y);
			ctx.rotate(p.rot);
			ctx.globalAlpha = Math.max(0, p.life);
			ctx.fillStyle = p.color;
			ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
			ctx.restore();
		}
		requestAnimationFrame(frame);
	};
	requestAnimationFrame(frame);
}

/**
 * Fire confetti after mint success. Double-rAF so ConfirmSheet portal can paint first.
 */
export function fireMintConfetti(): void {
	if (typeof window === "undefined") return;
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			runBurst();
		});
	});
}
